import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Modal, Result, Space, Tabs, Tag, Typography, message } from 'antd';
import { MailOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const emailRules = [{ required: true, message: '请输入邮箱地址' }, { type: 'email', max: 254, message: '请输入有效的邮箱地址' }];
const passwordRules = [{ required: true, message: '请输入新密码' }, { min: 6, message: '密码至少 6 个字符' }, {
  validator: (_, value) => !value || new Blob([value]).size <= 72 ? Promise.resolve() : Promise.reject(new Error('密码不能超过 72 字节'))
}];
const codeRules = [{ required: true, message: '请输入邮箱验证码' }, { pattern: /^\d{6}$/, message: '请输入 6 位数字验证码' }];
const errorText = error => error.response?.data?.message || '操作失败，请检查网络后重试';
const quiet = { silentError: true };

function useMailStatus() {
  const [enabled, setEnabled] = useState(null);
  useEffect(() => {
    let active = true;
    api.get('/auth/email-capabilities', quiet).then(({ data }) => { if (active) setEnabled(data.enabled); }).catch(() => {});
    return () => { active = false; };
  }, []);
  return enabled;
}

function useCooldown() {
  const [until, setUntil] = useState(0);
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const tick = () => setSeconds(Math.max(0, Math.ceil((until - Date.now()) / 1000)));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [until]);
  return [seconds, (delay = 60) => { setSeconds(delay); setUntil(Date.now() + delay * 1000); }];
}

function NewPasswordFields() {
  return <>
    <Form.Item name="newPassword" label="新密码" rules={passwordRules} extra="至少 6 个字符，建议使用字母、数字和符号组合。">
      <Input.Password autoComplete="new-password" placeholder="请输入新密码" />
    </Form.Item>
    <Form.Item name="confirmPassword" label="确认新密码" dependencies={['newPassword']} rules={[
      { required: true, message: '请再次输入新密码' },
      ({ getFieldValue }) => ({ validator: (_, value) => !value || value === getFieldValue('newPassword')
        ? Promise.resolve() : Promise.reject(new Error('两次输入的密码不一致')) })
    ]}>
      <Input.Password autoComplete="new-password" placeholder="请再次输入新密码" />
    </Form.Item>
  </>;
}

function VerificationFields({ form, binding = false, onSend, enabled, busy }) {
  const [sending, setSending] = useState(false);
  const [seconds, startCooldown] = useCooldown();
  const [notice, setNotice] = useState(null);
  const send = async () => {
    try {
      const values = await form.validateFields(binding ? ['email', 'currentPassword'] : ['email']);
      setSending(true); setNotice(null);
      const { data } = await api.post(binding ? '/auth/email/send-code' : '/auth/forgot-password', values, quiet);
      startCooldown(data.retryAfter || 60);
      setNotice({ type: 'success', message: data.message });
      onSend?.();
    } catch (error) {
      if (error.errorFields) return;
      if (error.response?.status === 429) startCooldown(error.response.data?.retryAfter || 60);
      setNotice({ type: 'error', message: errorText(error) });
    } finally { setSending(false); }
  };
  return <>
    {enabled === false && <Alert className="security-notice" type="warning" showIcon message="邮件服务暂未启用，请联系管理员配置后再使用。" />}
    <Form.Item label="邮箱验证码" required>
      <div className="email-code-row">
        <Form.Item name="code" rules={codeRules} noStyle>
          <Input aria-label="邮箱验证码" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="6 位数字验证码" />
        </Form.Item>
        <Button onClick={send} loading={sending} disabled={busy || enabled === false || seconds > 0}>
          {seconds > 0 ? `${seconds} 秒后重发` : '发送验证码'}
        </Button>
      </div>
    </Form.Item>
    {notice && <Alert className="security-notice" showIcon {...notice} />}
  </>;
}

export function ResetPasswordForm({ email, onSuccess, onBack, onBusyChange }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const enabled = useMailStatus();
  useEffect(() => { onBusyChange?.(saving); }, [saving, onBusyChange]);
  if (done) return <Result status="success" title="密码已重置" subTitle="请使用新密码重新登录。" extra={<Button type="primary" onClick={onSuccess || onBack}>返回登录</Button>} />;
  const submit = async values => {
    setSaving(true); setError('');
    try {
      await api.post('/auth/reset-password', { email: values.email, code: values.code, newPassword: values.newPassword }, quiet);
      if (email && onSuccess) onSuccess(); else setDone(true);
    } catch (err) { setError(errorText(err)); }
    finally { setSaving(false); }
  };
  return <Form form={form} layout="vertical" onFinish={submit} initialValues={{ email }} disabled={saving} className="account-security-form">
    <Typography.Paragraph type="secondary">使用已验证的绑定邮箱接收验证码，验证码 10 分钟内有效。重置后需要重新登录。</Typography.Paragraph>
    <Form.Item name="email" label="绑定邮箱" rules={emailRules} normalize={value => value.trim().toLowerCase()}>
      <Input prefix={<MailOutlined />} type="email" autoComplete="email" placeholder="请输入已验证的绑定邮箱" readOnly={Boolean(email)} />
    </Form.Item>
    <VerificationFields form={form} enabled={enabled} busy={saving} />
    <NewPasswordFields />
    {error && <Alert className="security-notice" type="error" message={error} showIcon />}
    <Button type="primary" htmlType="submit" block loading={saving} disabled={enabled === false}>重置密码</Button>
    {onBack && <><Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>尚未验证邮箱或无法收信？请联系管理员重置密码。</Typography.Paragraph><Button type="link" onClick={onBack} block>返回登录</Button></>}
  </Form>;
}

export function EmailBindingCard() {
  const { user, loadUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(params.get('verifyEmail') === '1');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form] = Form.useForm();
  const enabled = useMailStatus();
  useEffect(() => { if (params.get('verifyEmail') === '1') setOpen(true); }, [params]);
  const close = () => {
    if (saving) return;
    setOpen(false); setError(''); form.resetFields();
    if (params.has('verifyEmail')) { const next = new URLSearchParams(params); next.delete('verifyEmail'); setParams(next, { replace: true }); }
  };
  const submit = async values => {
    setSaving(true); setError('');
    try {
      await api.put('/auth/email', values, quiet);
      await loadUser();
      message.success('邮箱已验证，可以用于找回密码');
      setOpen(false); form.resetFields();
      if (params.has('verifyEmail')) { const next = new URLSearchParams(params); next.delete('verifyEmail'); setParams(next, { replace: true }); }
    } catch (err) { setError(errorText(err)); }
    finally { setSaving(false); }
  };
  return <>
    <Card title={<Space><SafetyCertificateOutlined />账号安全</Space>} className="security-card">
      <div className="security-setting-row">
        <div style={{ minWidth: 0 }}>
          <Space wrap><Typography.Text strong>找回密码邮箱</Typography.Text><Tag color={user?.emailVerified ? 'green' : 'orange'}>{user?.emailVerified ? '已验证' : '待验证'}</Tag></Space>
          <div className="security-email">{user?.email || '尚未设置'}</div>
          <Typography.Text type="secondary">{user?.emailVerified ? '忘记密码时，可通过此邮箱找回。更换邮箱需要重新验证。' : '请先验证邮箱，忘记密码时才能自助找回。'}</Typography.Text>
        </div>
        <Button type={user?.emailVerified ? 'default' : 'primary'} onClick={() => { setError(''); setOpen(true); }}>
          {user?.emailVerified ? '更换邮箱' : '验证并绑定邮箱'}
        </Button>
      </div>
    </Card>
    <Modal title={user?.emailVerified ? '更换绑定邮箱' : '验证并绑定邮箱'} open={open} onCancel={close} footer={null}
      maskClosable={!saving} closable={!saving} keyboard={!saving} destroyOnHidden>
      <Form form={form} preserve={false} layout="vertical" initialValues={{ email: user?.email }} onFinish={submit} disabled={saving} className="account-security-form">
        <Typography.Paragraph type="secondary">输入当前登录密码，并验证您本人可以收信的邮箱。验证码 10 分钟内有效。</Typography.Paragraph>
        <Form.Item name="currentPassword" label="当前密码" rules={[{ required: true, message: '请输入当前密码' }]}>
          <Input.Password autoComplete="current-password" placeholder="用于确认是您本人操作" />
        </Form.Item>
        <Form.Item name="email" label="邮箱地址" rules={emailRules} normalize={value => value.trim().toLowerCase()}>
          <Input type="email" autoComplete="email" placeholder="请输入可接收邮件的邮箱" />
        </Form.Item>
        <VerificationFields form={form} binding enabled={enabled} busy={saving} />
        {error && <Alert className="security-notice" type="error" message={error} showIcon />}
        <Button type="primary" htmlType="submit" block loading={saving} disabled={enabled === false}>验证并保存</Button>
      </Form>
    </Modal>
  </>;
}

function CurrentPasswordForm({ onBusyChange }) {
  const { changePassword } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { onBusyChange?.(saving); }, [saving, onBusyChange]);
  const submit = async values => {
    setSaving(true); setError('');
    try {
      const result = await changePassword({ currentPassword: values.currentPassword, newPassword: values.newPassword });
      if (!result.success) setError(result.message);
    } finally { setSaving(false); }
  };
  return <Form layout="vertical" onFinish={submit} disabled={saving} className="account-security-form">
    <Typography.Paragraph type="secondary">修改成功后，请使用新密码重新登录。</Typography.Paragraph>
    <Form.Item name="currentPassword" label="当前密码" rules={[{ required: true, message: '请输入当前密码' }]}>
      <Input.Password autoComplete="current-password" placeholder="请输入当前密码" />
    </Form.Item>
    <NewPasswordFields />
    {error && <Alert className="security-notice" type="error" message={error} showIcon />}
    <Button block type="primary" htmlType="submit" loading={saving}>确认修改</Button>
  </Form>;
}

export function PasswordSettingsModal({ open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  return <Modal title="修改密码" open={open} onCancel={() => { if (!busy) onClose(); }} maskClosable={!busy} closable={!busy} keyboard={!busy} footer={null} destroyOnHidden>
    <Tabs destroyOnHidden items={[
      { key: 'password', label: '使用当前密码', disabled: busy, children: <CurrentPasswordForm onBusyChange={setBusy} /> },
      { key: 'email', label: '忘记密码', disabled: busy, children: user?.emailVerified
        ? <ResetPasswordForm email={user.email} onBusyChange={setBusy} onSuccess={() => { message.success('密码已重置，请重新登录'); logout({ silent: true }); }} />
        : <Result icon={<MailOutlined />} title="先验证绑定邮箱" subTitle="当前邮箱尚未验证。请先在个人资料中完成验证，或联系管理员重置密码。"
          extra={<Button type="primary" onClick={() => { onClose(); navigate('/profile?verifyEmail=1'); }}>前往验证邮箱</Button>} /> }
    ]} />
  </Modal>;
}
